import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useTimeContext } from "./DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Building2, Database, TrendingUp, IndianRupee, Wallet, XCircle, Box, ArrowUpRight, ArrowDownRight } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, Legend } from "recharts";
import AIPageInsight from "@/components/AIPageInsight";
import { PeriodBadge } from "@/components/Formatters";
import ProjectDetail from "@/components/ProjectDetail";
import ExpectedCashflow from "@/components/ExpectedCashflow";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => { if (n == null) return '--'; const a = Math.abs(n); const s = n < 0 ? '-' : ''; if (a >= 1e7) return `${s}${(a/1e7).toFixed(2)} Cr`; if (a >= 1e5) return `${s}${(a/1e5).toFixed(2)} L`; if (a >= 1e3) return `${s}${(a/1e3).toFixed(1)}K`; return s + a.toLocaleString(); };

export default function ProjectIntelligence() {
  const { period, refDate, token, currentMonthInfo } = useTimeContext();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { period };
      if (refDate) params.ref_date = refDate;
      const kpiRes = await axios.get(`${API}/kpi/executive`, { headers: { Authorization: `Bearer ${token}` }, params });
      setData(kpiRes.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- API/axios are module constants, setters are stable
  }, [token, period, refDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const d = data || {};
  const salesByProject = d.sales_by_project || [];
  const collByProject = d.collection_by_project || [];
  const cancelByProject = d.cancel_by_project || [];
  const hasData = salesByProject.length > 0 || d.total_stock_units > 0;

  // Build consolidated project map — start with ALL 16 master projects
  const masterNames = d.project_names || [];
  const projectMap = {};
  masterNames.forEach(name => {
    projectMap[name] = { name, sales: 0, units: 0, sba: 0, collection: 0, cancels: 0, cancelUnits: 0 };
  });
  salesByProject.forEach(p => {
    if (projectMap[p.name]) { projectMap[p.name].sales = p.value; projectMap[p.name].units = p.count; projectMap[p.name].sba = p.sba || 0; }
  });
  collByProject.forEach(p => {
    if (projectMap[p.name]) projectMap[p.name].collection = p.value;
  });
  cancelByProject.forEach(p => {
    if (projectMap[p.name]) { projectMap[p.name].cancels = p.value; projectMap[p.name].cancelUnits = p.count; }
  });

  // Add stock data from KPI (already filtered by dim_projects)
  const stockStatus = d.stock_status || {};

  const projects = Object.values(projectMap).sort((a, b) => b.sales - a.sales);
  const totalSales = projects.reduce((s, p) => s + p.sales, 0);
  const totalCollection = projects.reduce((s, p) => s + p.collection, 0);

  // Chart data for top projects
  const chartData = projects.slice(0, 10).map(p => ({
    name: p.name.length > 16 ? p.name.slice(0, 14) + '..' : p.name,
    fullName: p.name,
    sales: p.sales,
    collection: p.collection,
  }));

  const getPerf = (p) => {
    if (totalSales === 0) return { label: '--', color: 'text-slate-400', bg: 'bg-slate-100' };
    const share = p.sales / totalSales * 100;
    const cancelRate = p.sales > 0 ? p.cancels / p.sales * 100 : 0;
    if (share >= 10 && cancelRate < 10) return { label: 'High', color: 'text-emerald-700', bg: 'bg-emerald-50' };
    if (share >= 4) return { label: 'Medium', color: 'text-amber-700', bg: 'bg-amber-50' };
    return { label: 'Low', color: 'text-red-700', bg: 'bg-red-50' };
  };

  const aiContext = hasData ? `${projects.length} projects. Total sales: ${fmt(totalSales)}. Top: ${projects[0]?.name} (${fmt(projects[0]?.sales)}). Top 3 = ${totalSales > 0 ? ((projects.slice(0,3).reduce((s,p)=>s+p.sales,0)/totalSales)*100).toFixed(0) : 0}% share. Total stock: ${d.total_stock_units} units (${d.sold_units} sold, ${d.available_units} available, ${d.blocked_units} blocked).` : '';

  if (!hasData && !loading) return (
    <div className="space-y-5" data-testid="project-intelligence-page">
      <div><h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>Project Intelligence</h1></div>
      <div className="empty-state"><Database className="h-12 w-12 text-slate-300 mb-4" /><p className="text-base font-medium text-slate-500">No Project Data</p></div>
    </div>
  );

  return (
    <div className="space-y-4 sm:space-y-6 overflow-hidden" data-testid="project-intelligence-page">
      <div>
        <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>Project Intelligence</h1>
        <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
          Deep-dive performance across {projects.length} projects
        </p>
        <PeriodBadge period={period} currentMonthInfo={currentMonthInfo} />
      </div>

      <AIPageInsight token={token} period={period} refDate={refDate} context={aiContext} pageTitle="Project Intelligence" />

      {/* Summary KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5 sm:gap-4">
        <div className="kpi-card">
          <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Projects</p>
          <p className="text-lg sm:text-xl font-bold text-slate-900 mt-1" style={{ fontFamily: 'Manrope' }}>{projects.length}</p>
        </div>
        <div className="kpi-card">
          <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Total Revenue</p>
          <p className="text-lg sm:text-xl font-bold text-slate-900 mt-1" style={{ fontFamily: 'Manrope' }}>{fmt(totalSales)}</p>
        </div>
        <div className="kpi-card">
          <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Total Collection</p>
          <p className="text-lg sm:text-xl font-bold text-emerald-600 mt-1" style={{ fontFamily: 'Manrope' }}>{fmt(totalCollection)}</p>
        </div>
        <div className="kpi-card">
          <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Total Stock</p>
          <p className="text-lg sm:text-xl font-bold text-slate-900 mt-1" style={{ fontFamily: 'Manrope' }}>{(d.total_stock_units || 0).toLocaleString('en-IN')}</p>
          <p className="text-[10px] text-slate-400">{d.sold_units || 0} sold · {d.available_units || 0} avl · {d.blocked_units || 0} blocked</p>
        </div>
        <div className="kpi-card">
          <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Top 3 Share</p>
          <p className="text-lg sm:text-xl font-bold text-indigo-600 mt-1" style={{ fontFamily: 'Manrope' }}>{totalSales > 0 ? `${((projects.slice(0,3).reduce((s,p)=>s+p.sales,0)/totalSales)*100).toFixed(0)}%` : '--'}</p>
          <p className="text-[10px] text-slate-400">Revenue concentration</p>
        </div>
      </div>

      {/* Sales vs Collection Chart */}
      {chartData.length > 0 && (
        <Card className="bg-white rounded-2xl border-slate-200/60 shadow-sm">
          <CardHeader className="pb-3 border-b border-slate-100"><CardTitle className="text-xs sm:text-sm font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>Revenue vs Collection by Project</CardTitle></CardHeader>
          <CardContent className="pt-3 sm:pt-4">
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={chartData} layout="vertical" barCategoryGap="20%" margin={{ left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 9, fill: '#94A3B8' }} tickFormatter={fmt} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: '#475569' }} width={130} />
                <Tooltip contentStyle={{ borderRadius: '10px', border: '1px solid #E2E8F0', fontSize: '11px' }} formatter={(v, name) => [fmt(v), name]} />
                <Legend wrapperStyle={{ fontSize: '11px' }} />
                <Bar dataKey="sales" fill="#1D4ED8" name="Revenue" radius={[0, 4, 4, 0]} barSize={12} />
                <Bar dataKey="collection" fill="#10B981" name="Collection" radius={[0, 4, 4, 0]} barSize={12} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Expected Cashflow */}
      <ExpectedCashflow token={token} refDate={refDate} />

      {/* Project Cards */}
      <div>
        <h2 className="text-sm sm:text-base font-bold text-slate-900 mb-3" style={{ fontFamily: 'Manrope' }}>Project Performance</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {projects.map((p, i) => {
            const perf = getPerf(p);
            const share = totalSales > 0 ? (p.sales / totalSales * 100).toFixed(1) : 0;
            const cancelRate = p.sales > 0 ? (p.cancels / p.sales * 100).toFixed(1) : 0;
            const collRatio = p.sales > 0 ? (p.collection / p.sales * 100).toFixed(0) : 0;
            const maxSales = projects[0]?.sales || 1;
            const barPct = Math.round((p.sales / maxSales) * 100);
            return (
              <Card key={i} className="bg-white rounded-xl border-slate-200/60 shadow-sm hover:shadow-md transition-shadow cursor-pointer" data-testid={`project-card-${i}`} onClick={() => setSelectedProject(p.name)}>
                <CardContent className="p-4 sm:p-5">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className="h-10 w-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                        <Building2 className="h-5 w-5 text-[#1D4ED8]" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-sm font-bold text-slate-900 truncate" style={{ fontFamily: 'Manrope' }}>{p.name}</h3>
                        <p className="text-[10px] text-slate-400">{share}% of total revenue</p>
                      </div>
                    </div>
                    <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${perf.bg} ${perf.color}`}>{perf.label}</span>
                  </div>

                  {/* Revenue bar */}
                  <div className="mb-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[9px] font-semibold text-slate-400 uppercase">Revenue</span>
                      <span className="text-sm font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>{fmt(p.sales)}</span>
                    </div>
                    <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all duration-500 bg-[#1D4ED8]" style={{ width: `${barPct}%` }} />
                    </div>
                  </div>

                  {/* Metrics grid */}
                  <div className="grid grid-cols-4 gap-1.5 mb-3">
                    <div className="bg-emerald-50 rounded-lg p-2 text-center">
                      <p className="text-xs font-bold text-emerald-700" style={{ fontFamily: 'Manrope' }}>{fmt(p.collection)}</p>
                      <p className="text-[8px] text-emerald-600 font-medium">Collection</p>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-2 text-center">
                      <p className="text-xs font-bold text-blue-700" style={{ fontFamily: 'Manrope' }}>{p.units}</p>
                      <p className="text-[8px] text-blue-600 font-medium">Units</p>
                    </div>
                    <div className="bg-indigo-50 rounded-lg p-2 text-center">
                      <p className="text-xs font-bold text-indigo-700" style={{ fontFamily: 'Manrope' }}>{p.sba > 0 ? `₹${Math.round(p.sales / p.sba).toLocaleString('en-IN')}` : '--'}</p>
                      <p className="text-[8px] text-indigo-600 font-medium">₹/Sq.ft</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-2 text-center">
                      <p className="text-xs font-bold text-slate-700" style={{ fontFamily: 'Manrope' }}>{collRatio}%</p>
                      <p className="text-[8px] text-slate-500 font-medium">Coll. Ratio</p>
                    </div>
                  </div>

                  {/* SBA */}
                  {p.sba > 0 && <p className="text-[10px] text-slate-400 mb-2">SBA: {p.sba.toLocaleString('en-IN')} Sq.ft</p>}

                  {/* Cancellation */}
                  {p.cancels > 0 && (
                    <div className="flex items-center gap-1.5 text-[10px] text-red-600 bg-red-50 rounded-lg px-2.5 py-1.5 mb-2">
                      <XCircle className="h-3 w-3 flex-shrink-0" />
                      <span>Cancellations: {fmt(p.cancels)} ({p.cancelUnits} units · {cancelRate}%)</span>
                    </div>
                  )}
                  <p className="text-[10px] text-blue-600 font-medium text-center">Tap for detailed analysis</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      <ProjectDetail isOpen={!!selectedProject} onClose={() => setSelectedProject(null)} projectName={selectedProject} token={token} period={period} refDate={refDate} />
    </div>
  );
}
